import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { brandAssets } from '../data/mock';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComingSoonPage = ({ onAccessGranted, showAccessCode = false }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Access code state
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [showInput, setShowInput] = useState(false);
  
  // Email waitlist state
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Magic link state
  const [validating, setValidating] = useState(false);
  const [magicLinkResult, setMagicLinkResult] = useState(null);

  // Check for magic link token on mount
  useEffect(() => {
    const magicToken = searchParams.get('magic') || searchParams.get('token');
    if (magicToken) {
      validateMagicLink(magicToken);
    }
  }, [searchParams]);

  const validateMagicLink = async (token) => {
    setValidating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/public/magic-link/${token}`);
      const data = await response.json();
      
      if (data.valid) {
        // Store access info in sessionStorage
        sessionStorage.setItem('tapInAccess', 'granted');
        sessionStorage.setItem('tapInMagicAccess', JSON.stringify({
          type: data.type,
          access: data.access,
          name: data.name,
          email: data.email,
          expires_at: data.expires_at,
          token: token
        }));
        
        setMagicLinkResult({
          success: true,
          name: data.name,
          portals: data.access?.portals || []
        });
        
        // Grant access after a brief delay to show success message
        setTimeout(() => {
          if (onAccessGranted) {
            onAccessGranted();
          }
          
          // If there's a specific portal to redirect to, go there
          if (data.access?.portals?.length === 1) {
            navigate(`/portal/${data.access.portals[0].slug}`);
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setMagicLinkResult({
          success: false,
          error: data.error || 'Invalid link'
        });
      }
    } catch (err) {
      console.error('Error validating magic link:', err);
      setMagicLinkResult({
        success: false,
        error: 'Unable to validate link. Please try again.'
      });
    }
    
    setValidating(false);
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === '2026LAUNCH') {
      sessionStorage.setItem('tapInAccess', 'granted');
      if (onAccessGranted) {
        onAccessGranted();
      }
      window.location.href = '/';
    } else {
      setError('Invalid access code');
      setAccessCode('');
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setEmailError('Please enter a valid email');
      return;
    }
    setEmailSubmitting(true);
    setEmailError('');
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setEmailSuccess(true);
        setEmail('');
      } else {
        setEmailError(data.detail || 'Something went wrong');
      }
    } catch {
      setEmailError('Unable to submit. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };


  // Show validation loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <div className="relative mb-8">
          <div className="absolute inset-0 -m-4 rounded-full bg-blue-500/20 animate-pulse" />
          <img src={brandAssets.logo} alt="Tap In" className="w-24 h-24 relative z-10" />
        </div>
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Validating your access...</span>
        </div>
      </div>
    );
  }

  // Show magic link result
  if (magicLinkResult) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <div className="relative mb-8">
          {magicLinkResult.success ? (
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
          )}
        </div>
        
        {magicLinkResult.success ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome{magicLinkResult.name ? `, ${magicLinkResult.name}` : ''}!</h1>
            <p className="text-gray-500 mb-4">Access granted. Redirecting you now...</p>
            {magicLinkResult.portals?.length > 0 && (
              <p className="text-sm text-gray-400">
                Portal access: {magicLinkResult.portals.map(p => p.name).join(', ')}
              </p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-500 mb-6">{magicLinkResult.error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
            >
              Go Back
            </button>
          </>
        )}
      </div>
    );
  }

  // Default Coming Soon page
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
      {/* Pulsing Logo - BLUE */}
      <div className="relative mb-8">
        {/* Outer pulse ring */}
        <div className="absolute inset-0 -m-6 rounded-full bg-blue-400/20 animate-ping" style={{ animationDuration: '2s' }} />
        {/* Middle pulse ring */}
        <div className="absolute inset-0 -m-4 rounded-full bg-blue-500/15 animate-pulse" style={{ animationDuration: '1.5s' }} />
        {/* Inner pulse ring */}
        <div className="absolute inset-0 -m-2 rounded-full bg-blue-600/10 animate-pulse" />
        
        {/* Logo */}
        <div className="relative w-32 h-32 flex items-center justify-center">
          <img 
            src={brandAssets.logo} 
            alt="Tap In" 
            className="w-28 h-28 object-contain"
          />
        </div>
      </div>

      {/* Coming Soon Text */}
      <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
        Coming Soon
      </h1>

      {/* Access Code Section */}
      {showAccessCode && (
        <div className="mt-8 w-full max-w-xs">
          {!showInput ? (
            <button
              onClick={() => setShowInput(true)}
              className="w-full text-purple-600 hover:text-purple-700 font-medium text-sm underline underline-offset-2"
              data-testid="have-access-code-btn"
            >
              Have an access code?
            </button>
          ) : (
            <form onSubmit={handleAccessSubmit}>
              <div className="relative">
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => {
                    setAccessCode(e.target.value.toUpperCase());
                    setError('');
                  }}
                  placeholder="Enter access code"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all text-center text-lg tracking-widest uppercase bg-white text-black"
                  autoFocus
                  data-testid="access-code-input"
                />
              </div>
              
              {error && (
                <p className="text-red-500 text-sm text-center mt-2">{error}</p>
              )}
              
              <button
                type="submit"
                className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-all"
                data-testid="submit-access-code-btn"
              >
                Enter
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setShowInput(false);
                  setAccessCode('');
                  setError('');
                }}
                className="w-full mt-2 text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      )}

      {/* Email Waitlist */}
      <div className="mt-10 w-full max-w-xs">
        {emailSuccess ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-green-600 font-medium text-sm">You're on the list!</p>
          </div>
        ) : (
          <>
            <p className="text-gray-500 text-sm text-center mb-3">
              Be the first to know when we go live!
            </p>
            <form onSubmit={handleEmailSubmit} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all text-sm bg-white text-black"
                data-testid="waitlist-email-input"
              />
              <button
                type="submit"
                disabled={emailSubmitting}
                className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50"
                data-testid="waitlist-submit-btn"
              >
                {emailSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
            </form>
            {emailError && (
              <p className="text-red-500 text-xs text-center mt-2">{emailError}</p>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-8 text-center">
        <p className="text-gray-400 text-xs">
          © 2026 Tap In. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ComingSoonPage;
